
import React from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FilterIcon } from 'lucide-react';
import { SYMPTOMS } from '@/data/symptoms';

interface SymptomFilterProps {
  symptomData: any[];
  selectedSymptoms: string[];
  setSelectedSymptoms: React.Dispatch<React.SetStateAction<string[]>>;
}

export const SymptomFilter: React.FC<SymptomFilterProps> = ({ 
  symptomData, 
  selectedSymptoms, 
  setSelectedSymptoms 
}) => {
  // Get all unique symptoms from user data
  const availableSymptoms = React.useMemo(() => {
    const standardSymptoms = ['Fatigue', 'Headache', 'Bloating', 'Cramps', 
                          'Mood Swings', 'Insomnia', 'Low Energy', 'Cravings'];
                          
    // Also include any custom symptoms from user's data
    const customSymptoms = new Set<string>();
    symptomData.forEach(entry => {
      entry.symptoms.forEach((symptom: string) => {
        if (!standardSymptoms.includes(symptom) && symptom !== 'none') {
          customSymptoms.add(symptom);
        }
      });
    });
    
    return [...standardSymptoms, ...Array.from(customSymptoms)];
  }, [symptomData]);
  
  const toggleSymptom = (symptom: string) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      } else {
        return [...prev, symptom];
      }
    });
  };
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="text-hormony-charcoal" size="sm">
          <FilterIcon size={18} className="mr-1" />
          Symptoms ({selectedSymptoms.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4" align="start">
        <h3 className="font-medium mb-2">Select Symptoms</h3>
        <div className="max-h-60 overflow-y-auto space-y-2">
          {availableSymptoms.map((symptom) => (
            <div key={symptom} className="flex items-center space-x-2">
              <Checkbox
                id={`symptom-${symptom}`}
                checked={selectedSymptoms.includes(symptom)}
                onCheckedChange={() => toggleSymptom(symptom)}
              />
              <label
                htmlFor={`symptom-${symptom}`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                {symptom}
              </label>
            </div>
          ))}
          
          {availableSymptoms.length === 0 && (
            <p className="text-sm text-gray-500 italic">No symptoms tracked yet</p>
          )}
        </div>
        
        <div className="flex justify-between mt-4 pt-2 border-t">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedSymptoms([])}
          >
            Clear
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setSelectedSymptoms(availableSymptoms.slice(0, 3))}
          >
            Select Common
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
